import ViewSwitcher from '@anm/components/ViewSwitcher';
import Switch from '@anm/components/form/Switch';
import InlineSelection from '@anm/components/list/InlineSelection';
import SectionTitle from '@anm/components/video-landings/SectionTitle';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { VideoStatusCode } from '@anm/hosting-player';
import React, { useCallback, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CustomizationTabIndexes } from '../../../../../pages/video';
import { hostedVideoActions, hostedVideoSelectors, EmbedType } from '../../../../../store/modules/hostedVideo';
import EmbedPreview from '../../RightPanel/EmbedPreview';
import useTogglers from '../../hooks/useTogglers';

import EmbedInfo from './EmbedInfo';
import EmbedSizes from './EmbedSizes';
import PopoverEmbed from './PopoverEmbed';
import SnippetWithCopy from './SnippetWithCopy';
import { EmbedTabWrapper, InlineEmbed, LoaderWrapper, TypeSelection } from './Wrapper';
import { getStatuses } from './helpers';
import { useEmbeddedVideos, useEmbedCodeSnippet, useEmbedLimit } from './hooks';
import { landingsSelectors } from '../../../../../store/modules/landings';
import useIsWebinar from '../../../../../hooks/useIsWebinar';
import { TitleWithToggler } from '../..';

export type EmbedTabProps = {
  hostId: string;
  errorCode?: VideoStatusCode;
};
const INLINE_EMBED_INDEX = 0;
const POPOVER_EMBED_INDEX = 1;

type EmbedTypeIndexes = typeof INLINE_EMBED_INDEX | typeof POPOVER_EMBED_INDEX;

const embedTypeIndexMap: { [key in EmbedTypeIndexes]: EmbedType } = {
  0: 'inline',
  1: 'popover'
};

const EMBED_TYPES_LIST = ['Inline embed', 'Popover embed'];

const EmbedTab: FC<EmbedTabProps> = ({ hostId, errorCode }) => {
  const dispatch = useDispatch();
  const isVideoEmbedded = useSelector(hostedVideoSelectors.selectEmbedStatus);
  const isWebinarEmbedded = useSelector(landingsSelectors.selectIsWebinarEmbedded);
  const isWebinar = useIsWebinar();
  const isEmbedded = isWebinar ? isWebinarEmbedded : isVideoEmbedded;

  const [activeEmbedType, setActiveEmbedType] = useState(INLINE_EMBED_INDEX);

  const { embedLimit, embeddedCount, extraEmbedPrice, ...paymentProps } = useEmbeddedVideos();

  const [embedData, formattedSnippet] = useEmbedCodeSnippet();

  const [togglers] = useTogglers<CustomizationTabIndexes>({
    hostId,
    isWebinar
  });

  const { embed: embedToggler } = togglers;

  const isLimitReached = useEmbedLimit({
    embedLimit,
    embeddedCount
  });

  const embedInfoProps = getStatuses({
    embedLimit,
    isEmbedded,
    embeddedCount,
    isLimitReached,
    ...paymentProps
  });

  const handleEmbedTypeChange = useCallback((i: EmbedTypeIndexes) => {
    setActiveEmbedType(i);
    dispatch(hostedVideoActions.changeEmbedType(embedTypeIndexMap[i]));
  }, []);

  if (isEmbedded === undefined) {
    return <LoaderWrapper size="large" color="blue" />;
  }

  if (!embedData) return null;

  return (
    <EmbedTabWrapper>
      <TitleWithToggler>
        <SectionTitle title="Embed video" />
        {isMobileOrIPad() && (
          <Switch onChange={embedToggler.handler} disabled={embedToggler.disabled} checked={embedToggler.status} />
        )}
      </TitleWithToggler>
      <EmbedInfo
        {...{
          embedLimit,
          embeddedCount,
          extraEmbedPrice,
          ...embedInfoProps
        }}
      />
      {!isWebinar && (
        <TypeSelection>
          <InlineSelection list={EMBED_TYPES_LIST} activeItem={activeEmbedType} onChange={handleEmbedTypeChange} />
        </TypeSelection>
      )}
      <ViewSwitcher active={activeEmbedType}>
        <InlineEmbed>
          <EmbedSizes />
        </InlineEmbed>
        <PopoverEmbed />
      </ViewSwitcher>
      {isMobileOrIPad() && <EmbedPreview {...{ hostId, errorCode }} />}
      <SnippetWithCopy
        {...{
          embedData,
          isEmbedded,
          formattedSnippet
        }}
      />
    </EmbedTabWrapper>
  );
};

export default EmbedTab;
